<template>
  <div class="max-w-7xl mx-auto pb-10 relative">

    <div
      class="space-y-6 transition-all duration-300 animate-fade-in"
      :class="{ 'blur-sm pointer-events-none opacity-50': showModal }"
    >

      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 class="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">Compras & Entradas</h1>
          <p class="text-slate-500 dark:text-slate-400 text-sm">Registro de facturas de proveedores e ingreso de stock.</p>
        </div>
        <button
          @click="openCreate"
          class="flex items-center gap-2 px-5 py-2.5 bg-brand-600 hover:bg-brand-700 text-white font-bold rounded-xl shadow-lg shadow-brand-500/30 transition-all active:scale-95"
        >
          <Plus class="w-5 h-5" /> Nueva Entrada
        </button>
      </div>

      <div class="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex items-center gap-4">
        <div class="relative flex-1 max-w-md">
          <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            v-model="searchTerm"
            type="text"
            placeholder="Buscar por proveedor o factura..."
            class="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all"
          >
        </div>
      </div>

      <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full text-sm text-left">
            <thead class="text-xs text-slate-500 uppercase bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
              <tr>
                <th class="px-6 py-4 font-semibold">Fecha / Factura</th>
                <th class="px-6 py-4 font-semibold">Proveedor</th>
                <th class="px-6 py-4 font-semibold">Sucursal</th>
                <th class="px-6 py-4 font-semibold text-right">Total</th>
                <th class="px-6 py-4 font-semibold text-center">Estado</th>
                <th class="px-6 py-4 font-semibold text-right">Acciones</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 dark:divide-slate-800">
              <tr v-if="isLoading"><td colspan="6" class="px-6 py-8 text-center text-slate-500">Cargando...</td></tr>
              <tr v-else-if="filteredList.length === 0"><td colspan="6" class="px-6 py-8 text-center text-slate-500">No hay compras registradas.</td></tr>

              <tr v-else v-for="item in filteredList" :key="item.id" class="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors group">

                <td class="px-6 py-4">
                  <div class="font-bold text-slate-900 dark:text-white">{{ item.invoice_number || 'S/N' }}</div>
                  <div class="text-xs text-slate-500">{{ item.purchase_date }}</div>
                </td>

                <td class="px-6 py-4 font-medium text-slate-700 dark:text-slate-300">
                  {{ suppliersMap[item.supplier] || 'Proveedor Desconocido' }}
                </td>

                <td class="px-6 py-4 text-slate-500">
                  {{ branchesMap[item.branch] || '---' }}
                </td>

                <td class="px-6 py-4 text-right font-bold text-slate-900 dark:text-white">
                  ${{ Number(item.total).toFixed(2) }}
                </td>

                <td class="px-6 py-4 text-center">
                  <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                    :class="item.status === 'COMPLETED'
                      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400'
                      : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'">
                    {{ item.status === 'COMPLETED' ? 'Procesado' : 'Borrador' }}
                  </span>
                </td>

                <td class="px-6 py-4 text-right">
                  <button v-if="item.status === 'DRAFT'" @click="openEdit(item)" class="p-2 text-slate-400 hover:text-brand-600 rounded-lg">
                    <Edit class="w-4 h-4" />
                  </button>
                  <span v-else class="text-xs text-slate-400 italic">Cerrado</span>
                </td>

              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <PurchaseForm
      :show="showModal"
      :purchaseToEdit="selectedItem"
      @close="showModal = false"
      @saved="loadData"
    />

  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { Search, Plus, Edit } from 'lucide-vue-next';
import apiClient from '@/api/client';
import { SecureStorage } from '@/utils/secureStorage';
import PurchaseForm from './Form.vue';

const list = ref([]);
const suppliersMap = ref({});
const branchesMap = ref({});
const isLoading = ref(true);
const searchTerm = ref('');
const showModal = ref(false);
const selectedItem = ref(null);

const loadData = async () => {
  isLoading.value = true;
  try {
    const user = SecureStorage.getItem('llevo_user');
    if (!user?.company) return;

    const [resPurchases, resSuppliers, resBranches] = await Promise.all([
      apiClient.get(`/purchases?company=${user.company}`),
      apiClient.get(`/suppliers?company=${user.company}`),
      apiClient.get(`/branches?company=${user.company}`)
    ]);

    list.value = Array.isArray(resPurchases.data) ? resPurchases.data : (resPurchases.data.data || []);

    // Mapeos para mostrar nombres
    const suppliers = resSuppliers.data || [];
    suppliers.forEach(s => suppliersMap.value[s.id] = s.name);

    const branches = resBranches.data || [];
    branches.forEach(b => branchesMap.value[b.id] = b.name);

  } catch (e) {
    console.error(e);
  } finally {
    isLoading.value = false;
  }
};

onMounted(loadData);

const filteredList = computed(() => {
  if (!searchTerm.value) return list.value;
  const term = searchTerm.value.toLowerCase();
  return list.value.filter(i =>
    i.invoice_number?.toLowerCase().includes(term) ||
    suppliersMap.value[i.supplier]?.toLowerCase().includes(term)
  );
});

const openCreate = () => { selectedItem.value = null; showModal.value = true; };
const openEdit = (item) => { selectedItem.value = item; showModal.value = true; };
</script>

<style scoped>
@keyframes fade-in {
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fade-in {
  animation: fade-in 0.3s ease-out forwards;
}
</style>